import React, { useState } from 'react';
import { Helmet } from "react-helmet";

import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { getNames } from 'country-list';
import './customizesafari.css';
import useScrollToTop from './ScrollToTop';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const initialState = {
  name: '',
  email: '',
  phone: '',
  country: '',
  startDate: '',
  endDate: '',
  duration: '',
  adults: 1,
  children: 0,
  safariType: '',
  transport: '',
  accommodation: '',
  language: '',
  pickupLocation: '',
  destinations: [],
  otherDestination: '',
  activities: [],
  notes: '',
  budgetRange: '',
};

const destinationOptions = ['Maasai Mara', 'Lake Naivasha', 'Lake Nakuru', 'Amboseli','Giraffe Centre', 'Osiligi Medical Hospital'];
const activityOptions = ['Game Drive', 'Nature Walk', 'Boat Ride', 'Cultural Visit','Bird-watching', 'Hot Air Balloon(Ksh.500/person)'];
const budgetOptions = ['Below $500', '$500 – $1000', '$1000 – $2000', '$2000 – $3000', '$3000 – $5000', '$5000 - $10000', 'Above $10000'];

const CustomizeSafari = () => {
  const [formData, setFormData] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  useScrollToTop();

  const countries = getNames().map(country => ({ value: country, label: country }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name, value) => {
    setFormData(prev => {
      const current = prev[name];
      return {
        ...prev,
        [name]: current.includes(value)
          ? current.filter(item => item !== value)
          : [...current, value],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { startDate, endDate, duration } = formData;

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end <= start) {
        toast.error('End date must be after start date.');
        return;
      }
      const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
      if (duration && parseInt(duration, 10) !== diffDays) {
        toast.error(`You chose ${duration} days, but dates are ${diffDays} days apart.`);
        return;
      }
    }

   const payload = {
  name: formData.name,
  email: formData.email,
  phone: formData.phone,
  country: formData.country,
  travelDate: formData.startDate,
  endDate: formData.endDate,
  adults: formData.adults,
  children: formData.children,
  safariType: formData.safariType,
  transport: formData.transport,
  accommodation: formData.accommodation,
  language: formData.language,
  places: [
    ...formData.destinations,
    ...(formData.otherDestination ? [formData.otherDestination] : [])
  ],
  activities: formData.activities,
  budget: formData.budgetRange,
  currency: 'USD',
  message: formData.notes,
  formType: 'customize',
};


    setIsSubmitting(true);

    try {
      const res = await fetch('/backend/customized', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (res.ok) {
        toast.success('Safari customization request sent!');
        setFormData(initialState);
        setTimeout(() => navigate('/thankyou'), 2000);
      } else {
        toast.error(result.error || 'Failed to send request');
      }
    } catch (err) {
      console.error('Submission error:', err);
      toast.error('Server error. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="custom-safari-form" onSubmit={handleSubmit}>

      {/**SEO AND AI */}
      <Helmet>
  <title>Customize Your Kenya Safari | Tailor-Made Tours by Olenini Safaris</title>
  <meta name="description" content="Design your dream Kenya safari with Olenini Safaris. Choose destinations, travel dates, experiences, and accommodation. Perfect for honeymooners, families, birders and more." />
  <meta name="keywords" content="custom Kenya safari, tailor-made safari Kenya, design your safari Kenya, plan safari Kenya, honeymoon safari, family safari, photography safari" />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://oleninisafaris.com/customizesafari" />

  {/* Open Graph */}
  <meta property="og:title" content="Customize Your Kenya Safari | Olenini Safaris" />
  <meta property="og:description" content="Tailor your safari adventure across Kenya. Choose your destinations, accommodation and experiences with expert support from Olenini Safaris." />
  <meta property="og:image" content="https://oleninisafaris.com/images/wildbeast.jpg" />
  <meta property="og:url" content="https://oleninisafaris.com/customizesafari" />
  <meta property="og:type" content="website" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Customize Your Kenya Safari | Olenini Safaris" />
  <meta name="twitter:description" content="Design your dream safari itinerary with Olenini Safaris. Flexible, personalized tours with expert guides." />
  <meta name="twitter:image" content="https://oleninisafaris.com/images/wildbeast.jpg" />

  {/**STRUCTURED DAT */}

<script type="application/ld+json">
{`
{
  "@context": "https://schema.org",
  "@type": "Trip",
  "name": "Customize Your Kenya Safari",
  "description": "Tailor-made safari trips across Kenya with Olenini Safaris. Choose from destinations like Maasai Mara, Amboseli, Lake Naivasha and more. Ideal for couples, families, birdwatchers and photographers.",
  "touristType": ["Honeymooners", "Families", "Photographers", "Birdwatchers", "Luxury Travelers", "Budget Travelers"],
  "provider": {
    "@type": "TravelAgency",
    "name": "Olenini Tours & Safaris",
    "url": "https://oleninisafaris.com",
    "telephone": "+254702965315"
  },
  "hasPart": [
    {
      "@type": "Place",
      "name": "Maasai Mara National Reserve"
    },
    {
      "@type": "Place",
      "name": "Amboseli National Park"
    },
    {
      "@type": "Place",
      "name": "Lake Nakuru National Park"
    },
    {
      "@type": "Place",
      "name": "other tourist cites"
    }
  ]
}
`}
</script>


</Helmet>




      <ToastContainer />
      <h2 className="form-heading">Customize Your Safari</h2>
      <p className="form-subtext">Let us tailor your perfect adventure through Kenya.</p>

      {/* Personal Details */}
      <h3 className="form-section-title">Personal Details</h3>
      <label>
        Full Name
        <input type="text" name="name" value={formData.name} onChange={handleChange} required />
      </label>

      <label>
        Email
        <input type="email" name="email" value={formData.email} onChange={handleChange} required />
      </label>

      <label>
        Phone Number
        <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required />
      </label>

      <label>
        Country
        <Select
          options={countries}
          value={formData.country ? { value: formData.country, label: formData.country } : null}
          onChange={(selected) => setFormData(prev => ({ ...prev, country: selected ? selected.value : '' }))}
          placeholder="Select your country"
          isClearable
          className="country-dropdown"
        />
      </label>

      {/* Safari Preferences */}
      <h3 className="form-section-title">Safari Preferences</h3>
      <label>
        Safari Start Date
        <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} />
      </label>

      <label>
        Safari End Date
        <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} />
      </label>

     
      <label>
        Number of Adults
        <input type="number" name="adults" value={formData.adults} onChange={handleChange} min="1" />
      </label>

      <label>
        Number of Children (U 12)
        <input type="number" name="children" value={formData.children} onChange={handleChange} min="0" />
      </label>

      <label>
        Safari Type
        <select name="safariType" value={formData.safariType} onChange={handleChange}>
          <option value="">Select</option>
          <option value="honeymoon">Honeymoon</option>
          <option value="family">Family</option>
          <option value="solo">Solo</option>
          <option value="group">Group</option>
        </select>
      </label>

      <label>
        Preferred Transport
        <select name="transport" value={formData.transport} onChange={handleChange}>
          <option value="">Select</option>
          <option value="land-cruiser">Land Cruiser</option>
          <option value="minivan">Minivan</option>
          <option value="plane">Flight (Chartered/Local)</option>
        </select>
      </label>

      <label>
        Accommodation Type
        <select name="accommodation" value={formData.accommodation} onChange={handleChange}>
          <option value="">Select</option>
          <option value="budget">Budget</option>
          <option value="midrange">Mid-range</option>
          <option value="luxury">Luxury</option>
        </select>
      </label>

      <label>
        Preferred Language for Guide
        <input type="text" name="language" value={formData.language} onChange={handleChange} />
      </label>

      <label>
        Budget Range Per Person (USD)
        <select name="budgetRange" value={formData.budgetRange} onChange={handleChange} required>
          <option value="">Select</option>
          {budgetOptions.map((option, index) => (
            <option key={index} value={option}>{option}</option>
          ))}
        </select>
      </label>

      {/* Destinations */}
    <h3 className="form-section-title">Select Destinations</h3>
<div className="checkbox-group">
  {destinationOptions.map(dest => (
    <label key={dest} className="checkbox-label">
      <input
        type="checkbox"
        checked={formData.destinations.includes(dest)}
        onChange={() => handleCheckboxChange('destinations', dest)}
      />
      {dest}
    </label>
  ))}

  <div className="other-destination-wrapper">
    <label htmlFor="otherDestination" className="other-destination-subtitle">
      Other destination (please specify):
    </label>
    <input
      type="text"
      id="otherDestination"
      name="otherDestination"
      placeholder="Other destination..."
      value={formData.otherDestination}
      onChange={handleChange}
    />
  </div>
</div>


      {/* Activities */}
      <h3 className="form-section-title">Preferred Activities</h3>
      <div className="checkbox-group">
        {activityOptions.map(act => (
          <label key={act} className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.activities.includes(act)}
              onChange={() => handleCheckboxChange('activities', act)}
            />
            {act}
          </label>
        ))}
      </div>

    <label className="notes-label">
  Additional Notes / Special Requests
  <textarea
    name="notes"
    value={formData.notes}
    onChange={handleChange}
    placeholder="Anything we should know?"
    rows={4}
  />
</label>


      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Submitting...' : 'Submit Custom Safari Request'}
      </button>
    </form>
  );
};

export default CustomizeSafari;
